// src/components/russia-map/MapContainer.tsx

import RussiaMapSVG from "@/components/RussiaMapSVG";
import { RegionInfo } from "@/lib/russiaRegions";
import { useRef, useEffect } from "react";
import { getIsoCode, regionsData } from "@/lib/russiaRegions";

interface MapContainerProps {
  handleMouseEnter: (svgId: string) => void;
  handleMouseLeave: () => void;
  handleClick: (svgId: string) => void;
  selectedRegion: RegionInfo | null;
  hoveredRegion: RegionInfo | null;
  regionData: Record<string, any>;
  activeCards: string[];
  zoom: { scale: number; translateX: number; translateY: number };
  setZoom: (zoom: { scale: number; translateX: number; translateY: number }) => void;
  onBackgroundClick: () => void;
  onSearchZoom?: (callback: (svgId: string) => void) => void; // опциональный проп
}

export const MapContainer = ({
  handleMouseEnter,
  handleMouseLeave,
  handleClick,
  selectedRegion,
  hoveredRegion,
  regionData,
  activeCards,
  zoom,
  setZoom,
  onBackgroundClick,
  onSearchZoom,
}: MapContainerProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  const zoomToRegion = (svgId: string, onZoomComplete?: () => void) => {
    const attemptZoom = () => {
      if (!groupRef.current) {
        requestAnimationFrame(attemptZoom);
        return;
      }

      const path = groupRef.current.querySelector(`#${svgId}`) as SVGPathElement | null;
      if (!path) {
        console.warn("Path not found for svgId:", svgId);
        if (onZoomComplete) onZoomComplete();
        return;
      }

      const bbox = path.getBBox();
      if (!bbox || bbox.width === 0 || bbox.height === 0) {
        console.warn("Invalid bbox for region:", svgId);
        if (onZoomComplete) onZoomComplete();
        return;
      }

      const viewMinX = -25;
      const viewWidth = 1400;
      const viewHeight = 800;

      const centerX = viewMinX + viewWidth / 2;
      const centerY = viewHeight / 2;

      const paddingFactor = 0.15;
      const targetWidth = viewWidth * (1 - paddingFactor);
      const targetHeight = viewHeight * (1 - paddingFactor);

      const scaleX = targetWidth / bbox.width;
      const scaleY = targetHeight / bbox.height;
      let newScale = Math.min(scaleX, scaleY);
      newScale = Math.max(newScale, 2);
      newScale = Math.min(newScale, 6);

      const regionCenterX = bbox.x + bbox.width / 2;
      const regionCenterY = bbox.y + bbox.height / 2;

      let translateX = centerX - regionCenterX * newScale;
      let translateY = centerY - regionCenterY * newScale;

      const mapWidth = 1600;
      const mapHeight = 800;

      const minTranslateX = viewWidth - mapWidth * newScale;
      const minTranslateY = viewHeight - mapHeight * newScale;

      translateX = Math.min(Math.max(translateX, minTranslateX), 0);
      translateY = Math.min(Math.max(translateY, minTranslateY), 0);

      setZoom({
        scale: newScale,
        translateX,
        translateY,
      });

      if (onZoomComplete) {
        setTimeout(onZoomComplete, 400);
      }
    };

    requestAnimationFrame(attemptZoom);
  };

  const handleRegionClick = (svgId: string) => {
    const isoCode = getIsoCode(svgId);
    const info = regionsData[isoCode];

    if (!info) return;

    zoomToRegion(svgId, () => {
      handleClick(svgId);
    });
  };

  // Передача функции только для зума (без handleClick)
  useEffect(() => {
    if (onSearchZoom) {
      const zoomHandler = (svgId: string) => {
        console.log("Поиск: запускаем зум на", svgId);
        zoomToRegion(svgId);
      };

      onSearchZoom(zoomHandler);
    }
  }, [onSearchZoom]);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const viewMinX = -25;
    const viewWidth = 1400;
    const viewHeight = 800;
    const mapWidth = 1600;
    const mapHeight = 800;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 1.1 : 0.9;
      let newScale = Math.min(Math.max(zoom.scale * delta, 1.35), 6);

      const rect = svg.getBoundingClientRect();
      const mouseX = ((e.clientX - rect.left) / rect.width) * viewWidth + viewMinX;
      const mouseY = ((e.clientY - rect.top) / rect.height) * viewHeight;

      const newTranslateX = mouseX - (mouseX - zoom.translateX) * (newScale / zoom.scale);
      const newTranslateY = mouseY - (mouseY - zoom.translateY) * (newScale / zoom.scale);

      const minTranslateX = viewWidth - mapWidth * newScale;
      const minTranslateY = viewHeight - mapHeight * newScale;

      setZoom({
        scale: newScale,
        translateX: Math.min(Math.max(newTranslateX, minTranslateX), 0),
        translateY: Math.min(Math.max(newTranslateY, minTranslateY), 0),
      });
    };

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      isDragging.current = true;
      startPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current || zoom.scale <= 1.35) return;

      const dx = (e.clientX - startPos.current.x) / zoom.scale;
      const dy = (e.clientY - startPos.current.y) / zoom.scale;

      const newTranslateX = Math.min(Math.max(zoom.translateX + dx, viewWidth - mapWidth * zoom.scale), 0);
      const newTranslateY = Math.min(Math.max(zoom.translateY + dy, viewHeight - mapHeight * zoom.scale), 0);

      setZoom((prev) => ({ ...prev, translateX: newTranslateX, translateY: newTranslateY }));
      startPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    svg.addEventListener("wheel", handleWheel, { passive: false });
    svg.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      svg.removeEventListener("wheel", handleWheel);
      svg.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [zoom, setZoom]);

  return (
    <div
      className="lg:col-span-3 bg-slate-950/60 rounded-3xl overflow-hidden cursor-grab active:cursor-grabbing"
      onClick={onBackgroundClick}
    >
      <RussiaMapSVG
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        handleClick={handleRegionClick}
        selectedRegion={selectedRegion}
        hoveredRegion={hoveredRegion}
        regionData={regionData}
        activeCards={activeCards}
        svgRef={svgRef}
        groupRef={groupRef}
        zoom={zoom}
      />
    </div>
  );
};